/**
 * Leaflet Library Loader
 * Lazy-loads Leaflet and MarkerCluster for optimal bundle splitting
 *
 * This module is the single source of truth for all map-related dependencies.
 * Vite will automatically code-split this into a separate chunk.
 */

// Import Leaflet core
import L from 'leaflet'

// Import Leaflet CSS (will be extracted to CSS bundle)
import 'leaflet/dist/leaflet.css'

// Import MarkerCluster plugin
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

// Fix Leaflet's default icon path issue with bundlers
// This is a known issue when bundling Leaflet with Vite/Webpack
import iconUrl from 'leaflet/dist/images/marker-icon.png'
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import shadowUrl from 'leaflet/dist/images/marker-shadow.png'

// Configure default icon
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
})

// Export Leaflet instance
export { L }

// Export types for TypeScript
export type {
  Map,
  Marker,
  LatLng,
  LatLngBounds,
  DivIcon,
  Layer,
  LayerGroup,
  LeafletMouseEvent,
  TileLayer,
} from 'leaflet'

// Type augmentation for MarkerCluster
declare module 'leaflet' {
  function markerClusterGroup(options?: MarkerClusterGroupOptions): MarkerClusterGroup

  interface MarkerClusterGroupOptions {
    showCoverageOnHover?: boolean
    zoomToBoundsOnClick?: boolean
    spiderfyOnMaxZoom?: boolean
    removeOutsideVisibleBounds?: boolean
    animate?: boolean
    animateAddingMarkers?: boolean
    disableClusteringAtZoom?: number
    maxClusterRadius?: number
    polygonOptions?: L.PolylineOptions
    singleMarkerMode?: boolean
    spiderLegPolylineOptions?: L.PolylineOptions
    spiderfyDistanceMultiplier?: number
    iconCreateFunction?: (cluster: MarkerCluster) => L.DivIcon
    chunkedLoading?: boolean
    chunkInterval?: number
    chunkDelay?: number
    chunkProgress?: (processed: number, total: number, elapsed: number) => void
  }

  interface MarkerClusterGroup extends L.FeatureGroup {
    addLayer(layer: L.Layer): this
    removeLayer(layer: L.Layer): this
    clearLayers(): this
    getVisibleParent(marker: L.Marker): L.Marker | MarkerCluster
    refreshClusters(markers?: L.Marker | L.Marker[]): this
    getChildCount(): number
    getAllChildMarkers(): L.Marker[]
    hasLayer(layer: L.Layer): boolean
    zoomToShowLayer(layer: L.Marker, callback?: () => void): void
  }

  interface MarkerCluster extends L.Marker {
    getChildCount(): number
    getAllChildMarkers(): L.Marker[]
    spiderfy(): void
    unspiderfy(): void
  }
}

/**
 * Async loader for lazy-loading Leaflet
 * Use this for dynamic imports in components
 */
export async function loadLeaflet(): Promise<typeof L> {
  // Already loaded since this module imports it
  return L
}

/**
 * Create a marker cluster group
 */
export function createMarkerClusterGroup(options?: L.MarkerClusterGroupOptions): L.MarkerClusterGroup {
  return L.markerClusterGroup(options)
}

/**
 * Check if MarkerCluster is available
 */
export function isMarkerClusterAvailable(): boolean {
  return typeof L.markerClusterGroup === 'function'
}
